import React, { useState, useEffect, useMemo } from 'react';
import { Download, Lock, LogIn, Search, FileText, Package, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const APT_LABELS = { suite: 'Suite', bellavista: 'Bellavista', oasis: 'Oasis', crisalide: 'Crisalide', emerald: 'Emerald' };
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const AdminExport = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [allowedApartments, setAllowedApartments] = useState([]);
  const [error, setError] = useState('');
  const [apartment, setApartment] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('arrival_date');
  const [sortDir, setSortDir] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const API = process.env.REACT_APP_BACKEND_URL || '';

  const authParams = `password=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`;

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/guest/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error('Credenziali non valide');
      const data = await res.json();
      setAllowedApartments(data.apartments || []);
      setApartment(data.apartments?.[0] || '');
      setAuthenticated(true);
      setError('');
    } catch (err) {
      setError(err.message || 'Credenziali non valide');
    }
  };

  const loadData = async () => {
    if (!apartment) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/guest/admin/registrations?apartment=${apartment}&year=${year}&month=${month}&${authParams}`);
      if (!res.ok) throw new Error();
      setRegistrations(await res.json());
    } catch { setRegistrations([]); }
    setLoading(false);
  };

  useEffect(() => { if (authenticated && apartment) loadData(); }, [authenticated, apartment, year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let data = [...registrations];
    if (searchText.trim()) {
      const s = searchText.toLowerCase();
      data = data.filter(r =>
        (r.surname || '').toLowerCase().includes(s) ||
        (r.name || '').toLowerCase().includes(s) ||
        (r.email || '').toLowerCase().includes(s) ||
        (r.nationality || '').toLowerCase().includes(s)
      );
    }
    if (statusFilter !== 'all') data = data.filter(r => r.payment_status === statusFilter);
    data.sort((a, b) => {
      const va = a[sortField] || '';
      const vb = b[sortField] || '';
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }, [registrations, searchText, statusFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} className="inline ml-0.5" /> : <ChevronDown size={12} className="inline ml-0.5" />;
  };

  const handleExport = () => window.open(`${API}/api/guest/admin/export?apartment=${apartment}&year=${year}&month=${month}&${authParams}`, '_blank');
  const handlePoliceExport = () => window.open(`${API}/api/guest/admin/export-police?apartment=${apartment}&year=${year}&month=${month}&${authParams}`, '_blank');
  const handleBulkDocDownload = () => window.open(`${API}/api/guest/documents/bulk?apartment=${apartment}&year=${year}&month=${month}&${authParams}`, '_blank');
  const handleSingleDoc = (fileId) => window.open(`${API}/api/guest/document/${fileId}?${authParams}`, '_blank');

  const docsCount = filtered.filter(r => r.document_file_id).length;

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F7F9F9] flex items-center justify-center" data-testid="admin-login">
        <meta name="robots" content="noindex, nofollow" />
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-lg shadow-lg border border-[#E2E8F0] w-full max-w-sm">
          <Lock className="mx-auto mb-4 text-[#65c3ba]" size={32} />
          <h1 className="text-xl font-bold text-center text-[#0A1312] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Admin Area</h1>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email" className="mb-3" />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password" className="mb-4" />
          {error && <p className="text-red-500 text-sm mb-4" data-testid="admin-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#65c3ba] text-white hover:bg-[#3e716c]" data-testid="admin-login-btn">
            <LogIn size={16} className="mr-2" />Accedi
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9F9]" data-testid="admin-panel">
      <meta name="robots" content="noindex, nofollow" />
      <div className="bg-[#3e716c] text-white py-4 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-lg sm:text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Homewise Admin — Registrazioni Ospiti</h1>
          <span className="text-xs text-white/60">{email}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filters row 1 */}
        <div className="bg-white p-4 rounded-lg border border-[#E2E8F0] shadow-sm">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[140px]">
              <label className="text-[10px] text-[#0A1312]/60 font-medium uppercase tracking-wider">Appartamento</label>
              <Select value={apartment} onValueChange={setApartment}>
                <SelectTrigger data-testid="admin-apt-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedApartments.map(a => <SelectItem key={a} value={a}>{APT_LABELS[a] || a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <label className="text-[10px] text-[#0A1312]/60 font-medium uppercase tracking-wider">Mese</label>
              <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                <SelectTrigger data-testid="admin-month-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[90px]">
              <label className="text-[10px] text-[#0A1312]/60 font-medium uppercase tracking-wider">Anno</label>
              <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 ml-auto flex-wrap">
              <Button onClick={handleExport} size="sm" className="bg-[#65c3ba] text-white hover:bg-[#3e716c] text-xs" data-testid="admin-export-btn">
                <Download size={14} className="mr-1" />Excel
              </Button>
              <Button onClick={handlePoliceExport} size="sm" className="bg-[#3e716c] text-white hover:bg-[#0A1312] text-xs" data-testid="admin-police-export-btn">
                <Download size={14} className="mr-1" />Polizia
              </Button>
              {docsCount > 0 && (
                <Button onClick={handleBulkDocDownload} size="sm" variant="outline" className="text-xs border-[#65c3ba] text-[#3e716c]" data-testid="bulk-doc-download">
                  <Package size={14} className="mr-1" />Documenti ZIP ({docsCount})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Filters row 2 */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1312]/30" />
            <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Cerca per cognome, nome, email, nazionalità..." className="pl-9 text-sm" data-testid="admin-search" />
            {searchText && <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0A1312]/30 hover:text-[#0A1312]"><X size={14} /></button>}
          </div>
          <div className="min-w-[130px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="text-xs" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="paid">Pagato</SelectItem>
                <SelectItem value="pending">In attesa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-xs text-[#0A1312]/40 ml-auto">{filtered.length} di {registrations.length} registrazioni</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-x-auto shadow-sm">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-[#F7F9F9] border-b border-[#E2E8F0]">
              <tr>
                {[
                  { key: 'surname', label: 'Cognome' },
                  { key: 'name', label: 'Nome' },
                  { key: 'arrival_date', label: 'Arrivo' },
                  { key: 'departure_date', label: 'Partenza' },
                  { key: 'nationality', label: 'Nazionalità' },
                  { key: 'nights', label: 'Notti' },
                  { key: 'taxable_guests', label: '14+' },
                  { key: 'child_companions', label: '<14' },
                  { key: 'tax_amount', label: 'Tassa CHF' },
                  { key: 'payment_status', label: 'Pagamento' },
                  { key: null, label: 'Doc' },
                ].map(col => (
                  <th key={col.label} className={`px-3 py-2.5 text-left text-[10px] font-semibold text-[#0A1312] uppercase tracking-wider ${col.key ? 'cursor-pointer hover:text-[#65c3ba] select-none' : ''}`}
                    onClick={col.key ? () => toggleSort(col.key) : undefined}>
                    {col.label}{col.key && <SortIcon field={col.key} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i} className="border-b border-[#E2E8F0] hover:bg-[#F7F9F9]/50 transition-colors">
                  <td className="px-3 py-2 font-medium text-[#0A1312]">{r.surname}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-xs">{r.arrival_date?.split('-').reverse().join('/')}</td>
                  <td className="px-3 py-2 text-xs">{r.departure_date?.split('-').reverse().join('/')}</td>
                  <td className="px-3 py-2 text-xs">{r.nationality}</td>
                  <td className="px-3 py-2 text-center">{r.nights}</td>
                  <td className="px-3 py-2 text-center">{r.taxable_guests}</td>
                  <td className="px-3 py-2 text-center">{r.child_companions ?? 0}</td>
                  <td className="px-3 py-2 font-medium">CHF {r.tax_amount?.toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${r.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                      {r.payment_status === 'paid' ? 'Pagato' : 'In attesa'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.document_file_id ? (
                      <button onClick={() => handleSingleDoc(r.document_file_id)} className="text-[#65c3ba] hover:text-[#3e716c] flex items-center gap-1" title={r.document_filename || 'Scarica'} data-testid={`doc-download-${i}`}>
                        <FileText size={14} />
                        <span className="text-[10px] max-w-[60px] truncate">{r.document_filename?.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                      </button>
                    ) : <span className="text-[#0A1312]/20 text-[10px]">—</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={11} className="px-3 py-12 text-center text-[#0A1312]/40">{loading ? 'Caricamento...' : 'Nessuna registrazione trovata'}</td></tr>}
              {filtered.length > 0 && (
                <tr className="bg-[#F0F7F6] border-t-2 border-[#65c3ba]">
                  <td colSpan={8} className="px-3 py-2.5 text-right font-bold text-sm text-[#0A1312]">Totale tasse incassate:</td>
                  <td className="px-3 py-2.5 font-bold text-sm text-[#3e716c]">CHF {filtered.reduce((sum, r) => sum + (r.tax_amount || 0), 0).toFixed(2)}</td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminExport;
